<template lang="pug">
.histories-tab(v-cloak)
  .mb-3
  .history-period.d-flex.flex-row.align-items-center
    .title.mr-2 履歴範囲
    b-form-select.history-option(id="history-period" v-model="historyPeriod" :options="historyPeriodOptions" size="md" @change="changeHistoryPeriod")
  .mb-2
  InspectionReservationTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @onChangeApproved="onChangeApproved"
    @onEdit="onEdit"
    @onDelete="onDelete"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・氏名
          div ・申込者からの連絡事項
          div ・受付メモ
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";

import InspectionReservationsService, { InspectionReservationsGetModel } from "@/commons/services/InspectionReservations";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import InspectionReservationTable, {
  InspectionReservationTableField,
  InspectionReservationTableItem,
  FilterSetting
} from "@/pc/containers/inspectionReservations/InspectionReservationTable.vue";

/**
 * ### 過去の履歴タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    InspectionReservationTable
  }
})
export default class HistoriesTab extends Vue {
  /** 施設見学サービス */
  @Inject("InspectionReservationsService") inspectionReservationsService!: InspectionReservationsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 履歴範囲 */
  historyPeriod: number = HistoryPeriodOptionValue.ONE_MONTH;
  /** 履歴範囲選択肢 */
  historyPeriodOptions: Array<{ value: number | string; text: string }> = [
    { value: HistoryPeriodOptionValue.ONE_MONTH, text: "1ヵ月" },
    { value: HistoryPeriodOptionValue.THREE_MONTH, text: "3ヵ月" },
    { value: HistoryPeriodOptionValue.SIX_MONTH, text: "6ヵ月" },
    { value: HistoryPeriodOptionValue.ONE_YEAR, text: "1年" },
    { value: HistoryPeriodOptionValue.ALL, text: "全て" }
  ];

  /** 削除確認モーダル表示可否 */
  showDeleteConfirmModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** 削除対象の施設見学申込み日 */
  inspectionReservationsSortKey: string | null = null;

  /** 列名 */
  fields: Array<InspectionReservationTableField> = [];
  /** 行 */
  items: Array<InspectionReservationTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.inspectionReservation.pastReservationList.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.inspectionReservation.pastReservationList.pageOptions;

  /** 詳細検索条件 */
  showAdvancesFilesr: boolean = false;

  /**
   * ### タブ情報の更新
   */
  public async refresh(): Promise<void> {
    await this.changeHistoryPeriod();
    return;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `past-inspection-reservations-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<InspectionReservationTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.inspectionReservation.pastReservationList.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<InspectionReservationTableField> {
    let fields: Array<InspectionReservationTableField> = [];

    fields.push({ key: "approved", label: "受付", sortable: false, sortDirection: "desc", class: "approved text-center" });
    fields.push({ key: "applicationType", label: "申込者", sortable: false, sortDirection: "desc", class: "application-type text-center" });
    fields.push({ key: "applicationDatetime", label: "申込み日時", sortable: false, sortDirection: "desc", class: "application-datetime" });
    fields.push({ key: "datetime", label: "見学日時", sortable: false, sortDirection: "desc", class: "datetime" });
    fields.push({ key: "nameAndTel", label: "氏名／電話番号", sortable: false, sortDirection: "desc", class: "name-and-tel" });
    fields.push({ key: "applicantMessage", label: "申込者からの連絡事項", sortable: false, sortDirection: "desc", class: "applicant-message" });
    fields.push({ key: "receptionMemo", label: "受付メモ", sortable: false, sortDirection: "desc", class: "reception-memo" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["past-inspection-reservations-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    let today: Date = new Date();
    const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");
    today.setMonth(today.getMonth() - 1);
    const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
    this.setRows({ start, end });

    const records: Array<InspectionReservationsGetModel> = await this.getInspectionReservations({ start, end });
    const filterdRecords: Array<InspectionReservationsGetModel> = this.filteringFromExistDatetime(records);
    const items: Array<InspectionReservationTableItem> = this.createItems(filterdRecords);
    this.items = items;
    this.currentRows = this.items.length;

    this.isLoading = false;

    // サイドメニューを更新
    this.$emit("refresh");

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(params: { start: string; end: string } | undefined) {
    try {
      const response: Array<InspectionReservationsGetModel> = await this.inspectionReservationsService.gets(userStore, params);
      this.rows = this.filteringFromExistDatetime(response).length;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 見学日時が存在するか否かでふぉるたリングする
   */
  public filteringFromExistDatetime(records: Array<InspectionReservationsGetModel>): Array<InspectionReservationsGetModel> {
    return records.concat().filter((record: InspectionReservationsGetModel) => {
      if (record.datetime) {
        return true;
      } else {
        return false;
      }
    });
  }

  /**
   * ### 施設見学予約を取得する
   */
  public async getInspectionReservations(params: { start: string; end: string } | undefined): Promise<Array<InspectionReservationsGetModel>> {
    const response: Array<InspectionReservationsGetModel> = await this.inspectionReservationsService.gets(userStore, params);
    return response;
  }

  /**
   * ### 行を作成する
   */
  public createItems(records: Array<InspectionReservationsGetModel>): Array<InspectionReservationTableItem> {
    let items: Array<InspectionReservationTableItem> = [];

    for (const record of records) {
      items.push({
        ...record
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 期間変更のセレクトボックス値変更イベント
   *
   * - 履歴を取得しなおす
   */
  public async changeHistoryPeriod(): Promise<void> {
    this.isLoading = true;

    if (this.historyPeriod == HistoryPeriodOptionValue.ALL) {
      this.setRows(undefined);

      const records: Array<InspectionReservationsGetModel> = await this.getInspectionReservations(undefined);
      const filterdRecords: Array<InspectionReservationsGetModel> = this.filteringFromExistDatetime(records);
      const items: Array<InspectionReservationTableItem> = this.createItems(filterdRecords);
      this.items = items;
      this.currentRows = this.items.length;
    } else {
      let today: Date = new Date();
      const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");

      switch (this.historyPeriod) {
        case HistoryPeriodOptionValue.ONE_MONTH: {
          today.setMonth(today.getMonth() - 1);
          break;
        }
        case HistoryPeriodOptionValue.THREE_MONTH: {
          today.setMonth(today.getMonth() - 3);
          break;
        }
        case HistoryPeriodOptionValue.SIX_MONTH: {
          today.setMonth(today.getMonth() - 6);
          break;
        }
        case HistoryPeriodOptionValue.ONE_YEAR: {
          today.setFullYear(today.getFullYear() - 1);
          break;
        }
        default: {
          today.setDate(today.getDate() - 7);
          break;
        }
      }
      const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
      this.setRows({ start, end });

      const records: Array<InspectionReservationsGetModel> = await this.getInspectionReservations({ start, end });
      const filterdRecords: Array<InspectionReservationsGetModel> = this.filteringFromExistDatetime(records);
      const items: Array<InspectionReservationTableItem> = this.createItems(filterdRecords);
      this.items = items;
      this.currentRows = this.items.length;
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### 受付状態を更新する
   */
  public async onChangeApproved(item: InspectionReservationTableItem): Promise<void> {
    this.$emit("onChangeApproved", item);
    return;
  }

  /**
   * ### 施設見学編集画面へ遷移する
   */
  public onEdit(item: InspectionReservationTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 施設見学予約の削除確認モーダルを表示する
   */
  public onDelete(item: InspectionReservationTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

export enum HistoryPeriodOptionValue {
  UNKNOWN = 0,
  ONE_MONTH = 1,
  THREE_MONTH = 2,
  SIX_MONTH = 3,
  ONE_YEAR = 4,
  ALL = 5
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.histories-tab {
  .history-period {
    .title {
      font-weight: bold;
    }
    .history-option {
      width: 120px;
    }
  }
}
</style>
